"use client";

import {
  createContext,
  JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CONSENT_CATEGORIES,
  ConsentCategory,
  RELOAD_ON_DOWNGRADE,
  SWEEPABLE_CATEGORIES,
} from "lib/consent/ConsentTypes";

/**
 * When consent is denied, sweep repeatedly rather than once.
 *
 * GTM is deliberately ungated and writes `_gcl_au` from an `afterInteractive`
 * script, so a single sweep at mount races the tag that re-creates the cookie.
 * These passes cover the realistic write window (initial load through late tag
 * fires) without leaving a permanent interval running.
 */
const RESWEEP_DELAYS_MS = [0, 500, 1500, 4000, 8000];

type OneTrustGlobals = {
  /**
   * The durable global. Format: ",C0001,C0003,". Present from OneTrust load
   * onwards. Note: `OnetrustActiveGroups` is a different, transient global that
   * is only reliably populated inside OptanonWrapper's own scope — reading it
   * from anywhere else yields undefined, so we deliberately do not use it.
   */
  OptanonActiveGroups?: string;
  OptanonWrapper?: () => void;
  OneTrust?: {
    OnConsentChanged?: (cb: () => void) => void;
  };
};

/**
 * Read the current active groups from OneTrust, safely.
 * Returns undefined when OneTrust has not loaded yet, which is distinct from
 * "loaded and everything denied" (an empty-ish string).
 */
const readActiveGroups = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as OneTrustGlobals).OptanonActiveGroups;
};

/**
 * Build a Set of granted category keys from the raw OneTrust string.
 * Splits on commas and compares exactly — a substring test would let an
 * unrelated custom group or host id containing "C0004" register as a grant,
 * and a false grant is a compliance failure, not just a bug.
 */
const parseGrantedCategories = (
  activeGroups: string | undefined,
): Set<ConsentCategory> => {
  const granted = new Set<ConsentCategory>();
  if (!activeGroups) return granted;

  const active = new Set(
    activeGroups
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean),
  );

  (Object.keys(CONSENT_CATEGORIES) as ConsentCategory[]).forEach((key) => {
    if (active.has(CONSENT_CATEGORIES[key])) {
      granted.add(key);
    }
  });
  return granted;
};

type ConsentContextValue = {
  /** True once OneTrust has resolved consent at least once this page load. */
  resolved: boolean;
  /** Returns whether a given category is currently granted. */
  hasConsent: (category: ConsentCategory) => boolean;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

type ConsentProviderProps = {
  children: ReactNode;
  /**
   * Optional hook for clearing cookies of downgraded categories.
   * If OneTrust's own auto-deletion is enabled in the console, leave this
   * undefined and rely on the reload alone. If not, pass the legacy
   * ecoDeleteCookie/eraseCookie routine here, scoped to the downgraded categories.
   */
  onDowngrade?: (downgraded: ConsentCategory[]) => void;
  /**
   * Clears cookies for categories that are currently denied, on every page load
   * rather than only on the downgrade transition.
   *
   * Required because GTM is ungated (client request) and has no Consent Mode
   * wiring, so it re-creates its cookies after the post-downgrade reload — which
   * makes the one-shot `onDowngrade` sweep a no-op in practice.
   */
  onResweep?: (denied: ConsentCategory[]) => void;
  /**
   * Escape hatch for tests / SSR-heavy pages. When false, the provider still
   * renders children but never reads OneTrust or reloads.
   */
  enabled?: boolean;
};

export const ConsentProvider = ({
  children,
  onDowngrade,
  onResweep,
  enabled = true,
}: ConsentProviderProps): JSX.Element => {
  // Store the raw OneTrust string rather than a derived Set. A fresh Set would
  // change identity on every sync even when consent is unchanged, which would
  // restart the re-sweep timers below on each poll tick.
  const [activeGroups, setActiveGroups] = useState<string | undefined>(
    undefined,
  );

  const granted = useMemo(
    () => parseGrantedCategories(activeGroups),
    [activeGroups],
  );
  // Only "resolved" once OneTrust has actually reported groups. Treating the
  // initial undefined as resolved would defeat its only purpose: telling
  // "not loaded yet" apart from "loaded and denied".
  const resolved = activeGroups !== undefined;

  // Track the previous granted set across OneTrust callbacks so we can detect
  // downgrades (was granted, now not). Ref, not state — we don't want a render.
  const prevGrantedRef = useRef<Set<ConsentCategory>>(new Set());
  // Guard so a single page load only ever triggers one reload.
  const reloadingRef = useRef(false);

  const sync = useCallback(() => {
    const active = readActiveGroups();
    const nextGranted = parseGrantedCategories(active);
    const prev = prevGrantedRef.current;

    // Detect downgrades on reload-sensitive categories.
    const downgraded = RELOAD_ON_DOWNGRADE.filter(
      (cat) => prev.has(cat) && !nextGranted.has(cat),
    );

    if (downgraded.length > 0 && !reloadingRef.current) {
      reloadingRef.current = true;

      // OneTrust writes OptanonConsent/OptanonAlertBoxClosed asynchronously,
      // AFTER it notifies listeners. Reloading synchronously here therefore
      // tears the page down before the user's choice is persisted — on the next
      // load there is no consent cookie, the opt-out geolocation rule re-grants
      // every category, and the rejection is silently discarded. Wait for the
      // cookie to land (bounded) before clearing and reloading.
      const PERSIST_TIMEOUT_MS = 3000;
      const PERSIST_POLL_MS = 100;
      const startedAt = Date.now();

      const finish = () => {
        // Clear cookies for the downgraded categories (if caller provided it).
        // Done last so scripts still running cannot immediately re-set them.
        try {
          onDowngrade?.(downgraded);
        } catch (e) {
          // Never let cookie cleanup block the reload.
          // eslint-disable-next-line no-console
          console.error("[consent] onDowngrade failed", e);
        }

        // Reload so non-teardownable scripts (APM, Cloud SDK, live tags) stop.
        // After reload, gating re-applies at the new lower consent level.
        window.location.reload();
      };

      const waitForPersist = () => {
        const persisted = document.cookie.includes("OptanonConsent");
        if (persisted || Date.now() - startedAt > PERSIST_TIMEOUT_MS) {
          finish();
          return;
        }
        window.setTimeout(waitForPersist, PERSIST_POLL_MS);
      };

      waitForPersist();
      return; // don't bother updating state; we're navigating away
    }

    prevGrantedRef.current = nextGranted;
    // Hold the last resolved value if OneTrust ever reports undefined after
    // having resolved — `resolved` must never flip back to false.
    setActiveGroups((prev) => (active === undefined ? prev : active));
  }, [onDowngrade]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;

    const win = window as unknown as OneTrustGlobals;
    let disposed = false;
    let pollId: ReturnType<typeof setInterval> | undefined;

    // Seed prev with whatever consent already exists so the FIRST sync isn't
    // misread as a downgrade (page-load grant should never look like revoke).
    prevGrantedRef.current = parseGrantedCategories(readActiveGroups());

    // Case 1: OneTrust already resolved before this mounted.
    sync();

    // Case 2: chain OptanonWrapper if we happen to own it.
    //
    // This is best-effort ONLY and must never be the sole subscription. A
    // classic script that declares `function OptanonWrapper() {...}` at global
    // scope re-defines the property outright (CreateGlobalFunctionBinding uses
    // DefineOwnProperty, not Set), so it silently wins over anything we assign
    // here — and it cannot be defended against with a getter/setter. The site
    // currently ships exactly such a script from the Sitecore Script component,
    // injected after this provider mounts. Hence Case 3.
    const prevWrapper = win.OptanonWrapper;
    win.OptanonWrapper = function patchedOptanonWrapper() {
      if (typeof prevWrapper === "function") prevWrapper();
      sync();
    };

    // Case 3: the authoritative subscription. OneTrust.OnConsentChanged does not
    // depend on owning any global, so it survives the clobber above. OneTrust
    // loads client-side well after this mounts, so poll until it appears.
    const POLL_MS = 200;
    let lastSeenGroups = readActiveGroups();

    const trySubscribe = (): boolean => {
      if (typeof win.OneTrust?.OnConsentChanged !== "function") return false;
      win.OneTrust.OnConsentChanged(() => {
        if (!disposed) sync();
      });
      return true;
    };

    if (!trySubscribe()) {
      pollId = setInterval(() => {
        if (disposed) return;

        // Pick up the initial resolution (and any change that slipped past a
        // clobbered wrapper) by watching the value itself.
        const current = readActiveGroups();
        if (current !== lastSeenGroups) {
          lastSeenGroups = current;
          sync();
        }

        // Once the event API is live we no longer need to poll.
        if (trySubscribe()) {
          clearInterval(pollId);
          pollId = undefined;
        }
      }, POLL_MS);
    }

    return () => {
      disposed = true;
      if (pollId) clearInterval(pollId);
      // Only restore if we are still the owner — otherwise we'd clobber whoever
      // legitimately replaced us.
      if (win.OptanonWrapper?.name === "patchedOptanonWrapper") {
        win.OptanonWrapper = prevWrapper;
      }
    };
  }, [enabled, sync]);

  // Defensive re-sweep. Deleting cookies on the downgrade transition alone does
  // nothing durable while an ungated tag re-creates them on the very next load,
  // so re-clear denied categories whenever consent resolves to a denied state.
  useEffect(() => {
    if (!enabled || !resolved || !onResweep) return;
    // A reload is already in flight; onDowngrade owns the cleanup for it.
    if (reloadingRef.current) return;

    const denied = SWEEPABLE_CATEGORIES.filter((cat) => !granted.has(cat));
    if (denied.length === 0) return;

    const timers = RESWEEP_DELAYS_MS.map((delay) =>
      window.setTimeout(() => {
        try {
          onResweep(denied);
        } catch (e) {
          // A failed sweep must never break the page.
          // eslint-disable-next-line no-console
          console.error("[consent] re-sweep failed", e);
        }
      }, delay),
    );

    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [enabled, resolved, granted, onResweep]);

  const value = useMemo<ConsentContextValue>(
    () => ({
      resolved,
      hasConsent: (category: ConsentCategory) => granted.has(category),
    }),
    [resolved, granted],
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
};

export const useConsentContext = (): ConsentContextValue => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return ctx;
};
